import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Moon, Plus, Clock, Star, AlertTriangle,
  Trash2, Edit2, X, Check, CloudMoon, BedDouble
} from 'lucide-react';
import { toast } from 'sonner';
import TimelineButton from '../components/TimelineButton';
import { todayLocal } from '../utils/dateUtils';

const SleepTracker = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [stats, setStats] = useState(null);
  
  const [formData, setFormData] = useState({
    date: todayLocal(),
    bedtime: '',
    wake_time: '',
    total_hours: '',
    sleep_quality: 3,
    night_wakings: 0,
    nightmares: false,
    bedwetting: false,
    sleep_anxiety: false,
    insomnia: false,
    notes: ''
  });

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/sleep?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching sleep entries:', error);
      toast.error('Failed to load sleep entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/sleep/stats?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching sleep stats:', error);
    }
  };

  const calculateTotalHours = (bedtime, wakeTime) => {
    if (!bedtime || !wakeTime) return null;
    
    const [bedHour, bedMin] = bedtime.split(':').map(Number);
    const [wakeHour, wakeMin] = wakeTime.split(':').map(Number);
    
    let bedMinutes = bedHour * 60 + bedMin;
    let wakeMinutes = wakeHour * 60 + wakeMin;
    
    // If wake time is earlier than bedtime, assume next day
    if (wakeMinutes < bedMinutes) {
      wakeMinutes += 24 * 60;
    }
    
    const totalMinutes = wakeMinutes - bedMinutes;
    return Math.round((totalMinutes / 60) * 10) / 10;
  };

  const handleTimeChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    // Auto-calculate total hours when both times are set
    if (newFormData.bedtime && newFormData.wake_time) {
      const hours = calculateTotalHours(newFormData.bedtime, newFormData.wake_time);
      if (hours) {
        newFormData.total_hours = hours;
      }
    }
    
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        total_hours: formData.total_hours ? parseFloat(formData.total_hours) : null,
        sleep_quality: parseInt(formData.sleep_quality),
        night_wakings: parseInt(formData.night_wakings) || 0
      };

      if (editingEntry) {
        await axios.put(`${API}/sleep/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sleep entry updated!');
      } else {
        await axios.post(`${API}/sleep`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sleep entry saved!');
      }
      
      resetForm();
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error saving sleep entry:', error);
      toast.error('Failed to save sleep entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this sleep entry?')) return;
    
    try {
      await axios.delete(`${API}/sleep/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      bedtime: entry.bedtime || '',
      wake_time: entry.wake_time || '',
      total_hours: entry.total_hours || '',
      sleep_quality: entry.sleep_quality || 3,
      night_wakings: entry.night_wakings || 0,
      nightmares: entry.nightmares || false,
      bedwetting: entry.bedwetting || false,
      sleep_anxiety: entry.sleep_anxiety || false,
      insomnia: entry.insomnia || false,
      notes: entry.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      date: todayLocal(),
      bedtime: '',
      wake_time: '',
      total_hours: '',
      sleep_quality: 3,
      night_wakings: 0,
      nightmares: false,
      bedwetting: false,
      sleep_anxiety: false,
      insomnia: false,
      notes: ''
    });
    setEditingEntry(null);
    setShowForm(false);
  };

  const qualityLabels = ['', 'Very Poor', 'Poor', 'Fair', 'Good', 'Excellent'];
  const qualityColors = ['', 'text-red-500', 'text-orange-500', 'text-yellow-500', 'text-lime-500', 'text-green-500'];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-8" data-testid="sleep-tracker">
      <Button 
        onClick={() => navigate('/dashboard')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <Moon className="w-10 h-10 text-indigo-600" strokeWidth={1.5} />
              <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
                Sleep Tracker
              </h1>
            </div>
            <p className="text-[#57534E]">
              Track sleep patterns to identify correlations with symptoms
            </p>
          </div>
          <TimelineButton />
        </div>
      </div>

      {/* Stats Overview */}
      {stats && stats.total_entries > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <Clock className="w-6 h-6 mx-auto mb-2 text-indigo-500" />
            <p className="text-2xl font-bold text-[#2D2D2D]">
              {stats.average_hours ? stats.average_hours.toFixed(1) : '--'}
            </p>
            <p className="text-sm text-[#57534E]">Avg Hours</p>
          </Card>
          <Card className="p-4 text-center">
            <Star className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold text-[#2D2D2D]">
              {stats.average_quality ? stats.average_quality.toFixed(1) : '--'}/5
            </p>
            <p className="text-sm text-[#57534E]">Avg Quality</p>
          </Card>
          <Card className="p-4 text-center">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-orange-500" />
            <p className="text-2xl font-bold text-[#2D2D2D]">
              {stats.nights_with_wakings}
            </p>
            <p className="text-sm text-[#57534E]">Nights w/ Wakings</p>
          </Card>
          <Card className="p-4 text-center">
            <CloudMoon className="w-6 h-6 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold text-[#2D2D2D]">
              {stats.total_entries}
            </p>
            <p className="text-sm text-[#57534E]">Nights Tracked</p>
          </Card>
        </div>
      )}

      {/* Add Entry Button */}
      {!showForm && (
        <Button
          onClick={() => setShowForm(true)}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-6"
          data-testid="add-sleep-entry-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Log Last Night's Sleep
        </Button>
      )}

      {/* Entry Form */}
      {showForm && (
        <Card className="p-6 border-2 border-indigo-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-[#2D2D2D] flex items-center gap-2">
              <BedDouble className="w-5 h-5 text-indigo-600" />
              {editingEntry ? 'Edit Sleep Entry' : 'Log Sleep'}
            </h2>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
                required
                data-testid="sleep-date-input"
              />
            </div>

            {/* Times */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#2D2D2D] font-medium">Bedtime</Label>
                <Input
                  type="time"
                  value={formData.bedtime}
                  onChange={(e) => handleTimeChange('bedtime', e.target.value)}
                  className="mt-1"
                  data-testid="sleep-bedtime-input"
                />
              </div>
              <div>
                <Label className="text-[#2D2D2D] font-medium">Wake Time</Label>
                <Input
                  type="time"
                  value={formData.wake_time}
                  onChange={(e) => handleTimeChange('wake_time', e.target.value)}
                  className="mt-1"
                  data-testid="sleep-waketime-input"
                />
              </div>
            </div>

            {/* Total Hours */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Total Hours Slept</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="24"
                value={formData.total_hours}
                onChange={(e) => setFormData({ ...formData, total_hours: e.target.value })}
                placeholder="Auto-calculated or enter manually"
                className="mt-1"
                data-testid="sleep-hours-input"
              />
            </div>

            {/* Sleep Quality */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">
                Sleep Quality: <span className={qualityColors[formData.sleep_quality]}>{qualityLabels[formData.sleep_quality]}</span>
              </Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setFormData({ ...formData, sleep_quality: rating })}
                    className={`flex-1 py-3 rounded-lg border-2 transition-all ${
                      formData.sleep_quality === rating
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`sleep-quality-${rating}`}
                  >
                    <Star className={`w-5 h-5 mx-auto ${formData.sleep_quality >= rating ? 'fill-yellow-400 text-yellow-400' : 'text-stone-300'}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Night Wakings */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Night Wakings</Label>
              <Input
                type="number"
                min="0"
                max="20"
                value={formData.night_wakings}
                onChange={(e) => setFormData({ ...formData, night_wakings: parseInt(e.target.value) || 0 })}
                className="mt-1"
                data-testid="sleep-wakings-input"
              />
            </div>

            {/* PANDAS-specific Issues */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">Sleep Issues</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'nightmares', label: 'Nightmares/Night Terrors', icon: '😰' },
                  { key: 'bedwetting', label: 'Bedwetting', icon: '💧' },
                  { key: 'sleep_anxiety', label: 'Sleep Anxiety', icon: '😟' },
                  { key: 'insomnia', label: 'Insomnia', icon: '😵' }
                ].map(({ key, label, icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData({ ...formData, [key]: !formData[key] })}
                    className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-2 ${
                      formData[key]
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-stone-200 hover:border-stone-300 text-[#57534E]'
                    }`}
                    data-testid={`sleep-issue-${key}`}
                  >
                    <span className="text-lg">{icon}</span>
                    <span className="text-sm font-medium">{label}</span>
                    {formData[key] && <Check className="w-4 h-4 ml-auto" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional observations about sleep..."
                className="mt-1"
                rows={3}
                data-testid="sleep-notes-input"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="sleep-submit-btn"
              >
                {editingEntry ? 'Update Entry' : 'Save Entry'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Entries List */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-[#2D2D2D]">Recent Sleep Entries</h2>
        
        {loading ? (
          <p className="text-center text-[#57534E] py-8">Loading...</p>
        ) : entries.length === 0 ? (
          <Card className="p-8 text-center">
            <Moon className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-[#57534E]">No sleep entries yet. Start tracking tonight!</p>
          </Card>
        ) : (
          entries.map((entry) => (
            <Card key={entry.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`sleep-entry-${entry.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-[#2D2D2D]">{formatDate(entry.date)}</span>
                    {entry.total_hours && (
                      <span className="text-sm bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                        {entry.total_hours}h
                      </span>
                    )}
                    {entry.sleep_quality && (
                      <span className={`text-sm ${qualityColors[entry.sleep_quality]}`}>
                        {qualityLabels[entry.sleep_quality]}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-sm text-[#57534E]">
                    {entry.bedtime && <span>🌙 {entry.bedtime}</span>}
                    {entry.wake_time && <span>☀️ {entry.wake_time}</span>}
                    {entry.night_wakings > 0 && <span>⚠️ {entry.night_wakings} wakings</span>}
                  </div>
                  
                  {/* Issues Tags */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {entry.nightmares && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Nightmares</span>}
                    {entry.bedwetting && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Bedwetting</span>}
                    {entry.sleep_anxiety && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Sleep Anxiety</span>}
                    {entry.insomnia && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Insomnia</span>}
                  </div>
                  
                  {entry.notes && (
                    <p className="text-sm text-[#57534E] mt-2 italic">{entry.notes}</p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(entry)}
                    data-testid={`edit-sleep-${entry.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(entry.id)}
                    className="text-red-500 hover:text-red-700"
                    data-testid={`delete-sleep-${entry.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SleepTracker;
